import React, { useEffect, useState } from 'react';

import { Button, Typography } from '@material-ui/core';
import axios from 'axios';
import { FormGroup, ListGroupItem } from 'react-bootstrap';

import { BackLink, Toastie } from '../../components/atoms';
import { Page } from '../../components/layouts';
import { PaymentOptions } from '../../components/organisms';
import { CampaignConsentDialog } from '../../components/partials';
import { l10nTools } from '../../utils/tools';
import { Track } from '../../weborder/analytics';
import * as Api from '../../weborder/Api';
import * as BasketControl from '../../weborder/BasketControl';
import { DispatchProps, StateProps } from '../../weborder/component/Checkout';
import LoadingPanel from '../../weborder/component/LoadingPanel';
import { BLANK } from '../../weborder/constants/Constants';
import { basketLogger } from '../../weborder/logger';
import * as Routes from '../../weborder/Routes';
import { Utilities } from '../../weborder/Utilities';
import { domUtils } from '../../weborder/utils';
import {
  EmailModal,
  Preorder,
  Price,
  SelectServiceButtons,
  SelectTimeModal,
  TableSelectModal,
  TipOptions,
  Voucher,
  VoucherEditLink,
} from './components';
import { checkoutUtils } from './tools';

type Props = StateProps & DispatchProps & Routes.Routes;

export function OrderCheckout(props: Props): JSX.Element {
  const {
    Account: account,
    AddItemToBasket: addItemToBasket,
    AddToast: addToast,
    AppConfig: appConfig,
    Basket: basket,
    basketLoaded,
    cancelCheckoutFlow,
    checkoutError,
    checkoutValidationErrors,
    continueCheckoutFlow,
    ChefNotes: chefNotes,
    createBasketSnapshot,
    Customer: customer,
    DeliveryLocation: deliveryLocation,
    EditTableNumbers: editTableNumbers,
    EditChefNotes: editChefNotes,
    getMenuByUrl,
    hasRequestFullNameFeatureFlag,
    Languages: languages,
    loading,
    LoadRestaurant: loadRestaurant,
    loginErrorBridge,
    loginSuccessBridge,
    menu,
    MenuUrl: menuUrl,
    Order: order,
    orderPayload,
    orderTipsTotal,
    orderTotalToPay,
    params,
    Payments: payments,
    PercentageTip: percentageTip,
    PickupLocationType: pickupLocationType,
    RemoveItemFromBasket: removeItemFromBasket,
    requestedTimeOrDefault,
    Restaurant: restaurant,
    SetCustomer: setCustomer,
    SetOrder: setOrder,
    SetRequestedTime: setRequestedTime,
    SetTip: setTip,
    setUserName,
    startCheckoutFlow,
    TableNumber: tableNumber,
    ToastMessages: toastMessages,
    translate,
    UpdateCustomer: updateCustomer,
    UserName: userName,
    VouchersInProgress: vouchersInProgress,
  } = props;

  const [chefNote, setChefNote] = useState({ type: 'loaded' });
  const [currentLang, setCurrentLang] = useState(
    l10nTools.getActiveLanguage(languages, 'en'),
  );
  const [menuLoadState, setMenuLoadState] = useState('NOT_LOADED');
  const [paymentError, setPaymentError] = useState(null);
  const [pendingVouchers, setPendingVouchers] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    //@ts-ignore todo: router type not declared on props
    const routeParams = props.router;
    setErrorFromUrl(routeParams);
  }, []);

  useEffect(() => {
    loadRestaurant(props);
  }, []);

  useEffect(() => {
    loggedInCheck();
  }, []);

  useEffect(() => {
    if (basketLoaded) {
      redirectToMenuScreenIfBelowMinimumOrderAmount();
      redirectToMenuIfRemovedAllItemsInBasket(basket.items);
    }
  }, [basketLoaded]);

  useEffect(() => {
    // if already available create the voucher
    const isPreOrderApplicable =
      !restaurant ||
      !restaurant.AcceptPreOrders ||
      (basket.requestedTime !== BLANK && basket.requestedTime !== '');

    if (account && customer && basket.items && isPreOrderApplicable) {
      getVoucher(account.PaymentAccountId, basket);
    }
  }, [account, customer, basket.items, restaurant]);

  useEffect(() => {
    setCurrentLang(l10nTools.getActiveLanguage(languages, 'en'));
  }, [languages?.active]);

  useEffect(() => {
    if (order.state === 'failed') {
      setOrder({
        state: 'none',
        submitType: 'NORMAL',
      });
    }
  }, [order.state]);

  useEffect(() => {
    const { Request, Require } = Api.EmailRequestMode;

    if (
      customer.status === 'success' &&
      !customer.data.IsGuest &&
      [Request, Require].includes(customer.data.EmailRequestMode)
    ) {
      setShowEmailModal(true);
    }
  }, [customer.status]);

  useEffect(() => {
    if (menu) {
      setMenuLoadState('LOADED');
    }
  }, [menu]);

  useEffect(() => {
    if (account) {
      getVoucher(account.PaymentAccountId, basket);
    }
  }, [basket.items.length, basket.requestedTime]);

  useEffect(() => {
    const newPendingVouchers = pendingVouchers.slice(1);
    setPendingVouchers(newPendingVouchers);
    basketLogger.debug('CHECKOUT PendingVouchers');
  }, [vouchersInProgress]);

  // The customer might load after this component is first mounted.
  // We need to update the name field in this case.
  // Also we have to check whether the minimum basket value required for delivery is satisfied
  // public UNSAFE_componentWillReceiveProps(nextProps: Props) {
  //   //If everything remains same, return
  //   if (
  //     isEqual(Basket.items, this.props.Basket.items) &&
  //     isEqual(Customer, this.props.Customer) &&
  //     isEqual(Account, this.props.Account) &&
  //     Restaurant === this.props.Restaurant &&
  //     isEqual(VouchersInProgress, this.props.VouchersInProgress) &&
  //     isEqual(nextProps.Basket.requestedTime, this.props.Basket.requestedTime)
  //   ) {
  //     return;
  //   }

  const toggleModal = () => setShowModal(!showModal);

  // TODO move this to 1 top component that handles global stuff like this
  const loggedInCheck = () => {
    const originalAlert = window.alert;
    window.alert = () => {
      return true;
    };

    axios
      .get('/account/accountdetails')
      .then(() => {
        window.alert = originalAlert;

        loginSuccessBridge();
      })
      .catch(() => {
        window.alert = originalAlert;

        loginErrorBridge();

        setCustomer({ status: 'none' });

        Utilities.navigateTo('/phone');
        axios.post('/Account/LogOff');
      });
  };

  const setErrorFromUrl = (routeParams) => {
    if (routeParams && routeParams.location.search) {
      const urlParams = new URLSearchParams(routeParams?.location?.search);
      const paymentError = urlParams.get('paymentError') as string;
      if (paymentError) {
        const errorMessage = checkoutUtils.getErrorMessage(paymentError);

        const error = {
          name: 'Payment_Error',
          message: errorMessage,
        } as any; // Error;
        setPaymentError(error);
      }
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    editTableNumbers(
      params.vrid,
      parseInt(e.target.value.replace(/\D/g, '')) || undefined,
    );
  };

  // If there is an exiting voucher apply the user
  const getVoucher = (
    paymentAccountId: number,
    basket: BasketControl.Data,
  ): any => {
    if (!menu && !restaurant) {
      return;
    }

    const something = checkoutUtils.getOrder(
      basket,
      orderPayload,
      orderTotalToPay,
      restaurant,
    );
    if (!something) {
      return;
    }

    const { order } = something;

    const preOrderAvailable = !restaurant.IsOpen && restaurant.AcceptPreOrders;
    if (preOrderAvailable && order.RequestedDeliveryOrPickupTimeUtc === BLANK) {
      return;
    }

    const currentBasketTotal = BasketControl.Total(basket.items);
    const shouldCreateOrder =
      currentBasketTotal > restaurant.MinimumOrder &&
      order.OrderItemVms.length !== 0;

    // Create order with auto-apply voucher if no voucher applied
    if (shouldCreateOrder && basket.voucher.status === 'none') {
      basketLogger.debug('CHECKOUT CHECK AUTO-APPLY');
    } else if (shouldCreateOrder && basket.voucher.status === 'valid') {
      basketLogger.debug('CHECKOUT ApplyExitingVoucher');
    }
  };

  const handleChangeDeliveryTime = (time: string) => {
    if (!menu || time === 'blank') {
      // TODO:DN something here, should never happen but be warned
      return;
    }
    setRequestedTime({ time, menuId: menu.MenuId });
  };

  const redirectToMenuIfRemovedAllItemsInBasket = (
    basketItems: BasketControl.Item[],
  ) => {
    if (!basketItems.length) {
      const restuarantUrl = checkoutUtils.getRestaurantUrl(params);
      Utilities.navigateTo(restuarantUrl);
      addToast(translate('Your_basket_is_empty') as string);
    }
  };

  const redirectToMenuScreenIfBelowMinimumOrderAmount = () => {
    const currentBasketTotal = BasketControl.Total(basket.items);

    if (!restaurant) {
      return;
    }

    const { MinimumOrder: minimumOrder, Currency: currency } = restaurant;

    let categoryNameTranslation = 'Restaurant_lowercase';

    if (appConfig.status === 'success') {
      const { Category } = appConfig.data;

      switch (Category) {
        case 0:
          categoryNameTranslation = 'Restaurant_lowercase';
          break;
        case 1:
          categoryNameTranslation = 'Cafe_lowercase';
          break;
        case 2:
          categoryNameTranslation = 'Store_lowercase';
          break;
        default:
          categoryNameTranslation = 'Restaurant_lowercase';
      }
    }

    if (currentBasketTotal < minimumOrder) {
      const restaurant = checkoutUtils.getRestaurantUrl(params);
      Utilities.navigateTo(restaurant);

      addToast(
        translate('Minimum_order_checkout', {
          categoryName: translate(categoryNameTranslation) as string,
          amount: BasketControl.ToCurrency(minimumOrder, currency, currentLang),
        }) as string,
      );
      return;
    }
  };

  const getBasketSnapshot = () => {
    const total: {
      currency: Api.Currency;
      order: Api.DeliveryOrder | Api.CollectionOrder;
      totalToPay: number;
      voucher: string;
    } = checkoutUtils.getOrder(
      basket,
      orderPayload,
      orderTotalToPay,
      restaurant,
    );

    const basketItems = checkoutUtils.calculateItemCounts(basket);
    const basketItemPrices = checkoutUtils.getItemTotalPrices(basketItems);

    const basketSnapshot = {
      basketItemPrices,
      basketItems: {
        ...basketItems,
      },
      deliveryFee: checkoutUtils.computeDeliveryFee(restaurant),
      displayTax: restaurant.DisplayTax,
      initialLang: currentLang,
      paymentType: account.Description,
      percentageTip: percentageTip,
      physicalRestaurantAddress: restaurant.PhysicalRestaurantAddress,
      processingFee: checkoutUtils.computePurchaseFee(
        account,
        basket,
        pickupLocationType,
        restaurant,
      ),
      processingFeeTaxRate: restaurant.ProcessingFeeTaxRate,
      taxes: checkoutUtils.computeTaxes(basket, restaurant),
      total,
      voucher: basket.voucher,
    };

    return basketSnapshot;
  };

  const handleSubmit = () => {
    const basketSnapshot: WebOrder.State.BasketSnapshot = getBasketSnapshot();
    createBasketSnapshot(basketSnapshot);

    startCheckoutFlow();
  };

  const handleChefNoteChange = (event: React.FormEvent<any>) => {
    const newValue: string = (event.target as any).value;
    setChefNote({
      type: 'custom',
    });

    editChefNotes(params.vrid, newValue);
  };

  const handleChefNoteBlur = (event: React.FormEvent<any>) => {
    const newValue: string = (event.target as any).value;

    if (newValue && newValue.length > 0) {
      Track('Orders', 'Add Note');
    }
  };

  const trackVoucher = (): any => {
    Track('Orders', 'Add Voucher');
  };

  const renderVoucher = (
    currency: Api.Currency,
    isDriveThrough: boolean,
  ): JSX.Element | null => {
    const { type, vrid, id, concessionStoreId } = params;
    const paymentAccountId = account && account.PaymentAccountId;

    if (!type || !vrid || !id) {
      return null;
    }

    if (basket && basket.voucher.status === 'loading') {
      return <Typography>{translate('Loading_voucher')}...</Typography>;
    }

    const linkProps = {
      disabled: false,
      tooltip: '',
    };

    if (typeof orderPayload === 'string') {
      switch (orderPayload) {
        case 'missing_pre_order_time': {
          linkProps.disabled = true;
          linkProps.tooltip = translate(
            'Please_select_a_time_before_adding_voucher',
            {
              type: translate(type) as string,
            },
          ) as string;
          break;
        }
      }
    }

    // if table select only and table not selected
    if (
      pickupLocationType === Api.PickupLocationType.TableService &&
      !isDriveThrough &&
      typeof tableNumber !== 'number'
    ) {
      const tableServiceCategory = !restaurant
        ? Api.TableServiceCategory.Generic
        : restaurant.DeliveryType === Api.DeliveryType.Collection
        ? restaurant.TableServiceCatagory || Api.TableServiceCategory.Generic
        : Api.TableServiceCategory.Generic;

      linkProps.disabled = true;
      linkProps.tooltip = translate(
        `Table_voucher_${tableServiceCategory}`,
      ) as string;
    }

    if (basket.voucher.status === 'none') {
      return (
        <VoucherEditLink
          concessionStoreId={concessionStoreId}
          dataFd={'add_voucher_link'}
          id={id}
          onClick={trackVoucher}
          paymentAccountId={paymentAccountId}
          type={type}
          vrId={vrid}
          {...linkProps}
        >
          {translate('Add_voucher_code')}
        </VoucherEditLink>
      );
    }

    const voucherItems = basket.voucher.items.map((i, idx) => (
      <ListGroupItem key={idx}>
        <Typography>
          {i.Name} {i.Description && `(${i.Description})`}
        </Typography>
        <Price
          Currency={currency}
          currentLanguage={currentLang}
          Price={i.Amount + i.TaxAmount}
        />
      </ListGroupItem>
    ));

    return (
      <FormGroup key="voucher">
        <div className="checkout__items">
          <ListGroupItem key="label">
            <Typography>{translate('Voucher')}</Typography>
          </ListGroupItem>

          {voucherItems}

          <ListGroupItem key="label-change">
            <VoucherEditLink
              concessionStoreId={concessionStoreId}
              dataFd={'edit_voucher_link'}
              id={id}
              onClick={trackVoucher}
              paymentAccountId={paymentAccountId}
              type={type}
              vrId={vrid}
              {...linkProps}
            >
              {translate('Change_voucher')}
            </VoucherEditLink>
          </ListGroupItem>
        </div>
      </FormGroup>
    );
  };

  if (order.state === 'paid' || customer.status !== 'success') {
    return;
  }

  if (appConfig.status !== 'success' || !restaurant) {
    return <LoadingPanel />;
  }

  if (menuLoadState === 'NOT_LOADED' && !menu && menuUrl) {
    getMenuByUrl(menuUrl);
    setMenuLoadState('LOADING');
  }

  const currency = (restaurant && restaurant.Currency) || Api.Currency.EUR;

  let deliveryFeeRow: JSX.Element | null = null;

  const deliveryFee: number = checkoutUtils.computeDeliveryFee(restaurant);
  if (deliveryFee > 0) {
    deliveryFeeRow = (
      <ListGroupItem className="flex">
        <Typography tabIndex={0} aria-label={`${translate('Delivery_fee')}`}>
          {translate('Delivery_fee')}
        </Typography>
        <Price
          Currency={currency}
          currentLanguage={currentLang}
          Price={deliveryFee}
        />
      </ListGroupItem>
    );
  }

  const onlineOrderingFees = checkoutUtils.computePurchaseFee(
    account,
    basket,
    pickupLocationType,
    restaurant,
  );

  let onlineProcessingCharge: JSX.Element | null = null;
  if (onlineOrderingFees > 0) {
    onlineProcessingCharge = (
      <ListGroupItem className="flex">
        <div tabIndex={0} aria-label={`${translate('Online_ordering_fee')}`}>
          <Typography>{translate('Online_ordering_fee')}</Typography>

          {restaurant.DisplayTax && restaurant.ProcessingFeeTaxRate > 0 ? (
            <Typography>
              (
              {translate('Includes_tax_of', {
                tax: `${restaurant.ProcessingFeeTaxRate}%`,
              })}
              )
            </Typography>
          ) : null}
        </div>
        <Price
          Currency={currency}
          currentLanguage={currentLang}
          Price={onlineOrderingFees}
        />
      </ListGroupItem>
    );
  }

  const basketTaxes = checkoutUtils.computeBasketTaxes(basket);
  const taxes = (restaurant.DeliveryFeeTaxAmount || 0) + basketTaxes;

  let taxesRow: JSX.Element | null = null;
  if (restaurant.DisplayTax) {
    taxesRow = (
      <ListGroupItem className="flex">
        <div tabIndex={0} aria-label={`${translate('Tax')}`}>
          <Typography>{translate('Tax')}</Typography>
        </div>
        <Price
          Currency={currency}
          currentLanguage={currentLang}
          Price={taxes}
        />
      </ListGroupItem>
    );
  }

  const tipEnabled = checkoutUtils.isTipEnabled(
    appConfig,
    customer,
    restaurant,
  );
  const usingTipablePaymentMethod = checkoutUtils.isTipablePaymentMethod(
    account,
  );
  const totalToPay = orderTotalToPay;
  const tipAmount = orderTipsTotal;

  let tipRow: JSX.Element | null = null;
  if (tipEnabled && usingTipablePaymentMethod) {
    tipRow = (
      <>
        <ListGroupItem className="flex">
          <div
            tabIndex={0}
            aria-label={`${translate('Tip_{0}', {
              PercentageTip: `${percentageTip}%`,
            })}`}
          >
            <Typography>
              (
              {translate('Tip_{0}', {
                PercentageTip: `${percentageTip}%`,
              })}
              )
            </Typography>
          </div>

          <Price
            Currency={currency}
            currentLanguage={currentLang}
            Price={tipAmount}
          />
        </ListGroupItem>

        {customer.status === 'success' && (
          <TipOptions
            onChange={setTip}
            // selectedTipPercentage={percentageTip}
            tipPercentageOptions={customer.data.TipPercentageOptions}
            translate={translate}
          />
        )}
      </>
    );
  }

  const { type, vrid, id, concessionStoreId } = params;
  const extraRouteParams = concessionStoreId ? `/${concessionStoreId}` : '';
  const backRoute = `/restaurant/${vrid}/${type}/${id}${extraRouteParams}`;

  const counts: any = checkoutUtils.calculateItemCounts(basket);

  // map object to an array of JSX elements
  const itemList: JSX.Element[] = [];

  for (const key in counts) {
    if (!counts.hasOwnProperty(key)) {
      continue;
    }

    const occurence: number = counts[key].occurence;
    const index: number = counts[key].index;
    const basketItem: BasketControl.Item = counts[key].item;
    const name: string = basketItem.Item.Name;
    const options: string = basketItem.Options.map((o) => o.Name).join(', ');
    const price: number = BasketControl.ItemTotal(basketItem);

    const subtractItem = () => {
      Track('Orders', 'Remove Item From Basket Via Checkout', {
        event_label: basketItem.Item.Name,
        virtualRestaurantId: params.vrid,
        menuId: menu.MenuId,
      });
      removeItemFromBasket({ menuId: menu.MenuId, index });
    };

    const addItem = () => {
      Track('Orders', 'Add Item To Basket Via Checkout', {
        event_label: basketItem.Item.Name,
        virtualRestaurantId: params.vrid,
        name: basketItem.Item.Name,
        itemType: basketItem.Options.length === 0 ? 'simple' : 'complex',
        menuId: menu.MenuId,
      });

      addItemToBasket(basketItem);
    };

    const optionsList = options.split(',').map((option, index) => (
      <li key={index} className="clear-margins list-unstyled">
        <Typography>{option}</Typography>
      </li>
    ));

    const item: JSX.Element = (
      <ListGroupItem key={key} className="flex">
        <div tabIndex={0} aria-label={`${occurence} ${name}`}>
          <Typography>{`${occurence} x ${name}`}</Typography>
          {options && (
            <ul className="list-unstyled checkout__items__options">
              {optionsList}
            </ul>
          )}
        </div>
        <div>
          <Typography>
            {BasketControl.ToCurrency(price * occurence, currency, currentLang)}
          </Typography>
          <div>
            <Button
              aria-label={`${translate('Remove')} ${name}`}
              onClick={subtractItem}
              tabIndex={0}
            >
              <i key={`minus-icon-${key}`} />
            </Button>
            <Typography>{occurence}</Typography>
            <Button
              tabIndex={0}
              onClick={addItem}
              aria-label={`${translate('Add')} ${name}`}
            >
              <i key={`plus-icon-${key}`} className="icon_plus_alt2" />
            </Button>
          </div>
        </div>
      </ListGroupItem>
    );

    itemList.push(item);
  }

  const { Category } = appConfig.data;

  let categoryNoteTranslation = '';
  let categoryNoteExample = translate('Notes_example') as string;

  switch (Category) {
    case 0:
      categoryNoteTranslation = 'Chef';
      categoryNoteExample = translate('Notes_example') as string;
      break;
    case 1:
      categoryNoteTranslation = 'Barista';
      categoryNoteExample = translate('Notes_example') as string;
      break;
    case 2:
      categoryNoteTranslation = 'Store_lowercase';
      categoryNoteExample = translate('Notes_example') as string;
      break;
    default:
      categoryNoteTranslation = 'Chef';
      categoryNoteExample = translate('Notes_example') as string;
  }

  const tableServiceIndex =
    restaurant.DeliveryType === Api.DeliveryType.Collection &&
    restaurant.PickupLocationTypes.findIndex(
      (PickupLocation) =>
        PickupLocation === Api.PickupLocationType.TableService,
    );

  const dineInIndex =
    restaurant.DeliveryType === Api.DeliveryType.Collection &&
    restaurant.PickupLocationTypes.findIndex(
      (PickupLocation) => PickupLocation === Api.PickupLocationType.DineIn,
    );

  const takeOutIndex =
    restaurant.DeliveryType === Api.DeliveryType.Collection &&
    restaurant.PickupLocationTypes.findIndex(
      (PickupLocation) => PickupLocation === Api.PickupLocationType.TakeOut,
    );

  const isTableServiceOnly =
    restaurant.DeliveryType === Api.DeliveryType.Collection &&
    restaurant.PickupLocationTypes.length === 1 &&
    tableServiceIndex != -1;

  const isDineInAndTakeOut = dineInIndex != -1 && takeOutIndex != -1;

  const isTableServiceAndTakeOut =
    tableServiceIndex != -1 && takeOutIndex != -1;

  const isDriveThrough =
    restaurant.DeliveryType === Api.DeliveryType.Collection &&
    restaurant.TableServiceCatagory === Api.TableServiceCategory.DriveThrough;

  const isDriveThroughOnly =
    restaurant.DeliveryType === Api.DeliveryType.Collection &&
    restaurant.PickupLocationTypes.length === 1 &&
    tableServiceIndex != -1 &&
    restaurant.TableServiceCatagory === Api.TableServiceCategory.DriveThrough;

  const serviceTypes = {
    isDelivery: params.type === 'delivery',
    isDriveThrough: isDriveThrough,
    isDriveThroughOnly: isDriveThroughOnly,
    isTableServiceOnly: isTableServiceOnly,
    isDineInAndTakeOut: isDineInAndTakeOut,
    isTableServiceAndTakeOut: isTableServiceAndTakeOut,
  };

  const tableServiceCategory =
    restaurant.DeliveryType === Api.DeliveryType.Collection
      ? restaurant.TableServiceCatagory || Api.TableServiceCategory.Generic
      : Api.TableServiceCategory.Generic;

  const pickupLocationOptions =
    restaurant.DeliveryType === Api.DeliveryType.Collection &&
    restaurant.PickupLocationOptions &&
    restaurant.PickupLocationOptions.length > 0
      ? restaurant.PickupLocationOptions
      : undefined;

  const preOrderAvailable = !restaurant.IsOpen && restaurant.AcceptPreOrders;

  const allowPreOrdersAndTableService =
    restaurant.DeliveryType === Api.DeliveryType.Collection
      ? restaurant.AllowPreOrdersAndTableService || true
      : true;

  const disabled =
    !!checkoutValidationErrors.length ||
    (preOrderAvailable &&
      !allowPreOrdersAndTableService &&
      pickupLocationType === Api.PickupLocationType.TableService);

  const missingTime = checkoutValidationErrors.includes('requested_time');
  const missingName = checkoutValidationErrors.includes('name');
  const missingTableNumber = checkoutValidationErrors.includes('table_number');

  const toggleEmailModal = () => {
    setShowEmailModal(!showEmailModal);
  };

  const renderEmailModal = (customerData: Api.Customer) => (
    <EmailModal
      AppConfig={appConfig}
      Close={toggleEmailModal}
      EmailRequestMode={customerData.EmailRequestMode}
      ShowEmailModal={showEmailModal}
      translate={translate}
      UpdateCustomer={updateCustomer}
    />
  );

  return (
    <>
      <Page pageName="order-checkout">
        <Toastie messages={toastMessages} />

        <header>
          <BackLink label={translate('Back') as any} linkPath={backRoute} />

          <Typography variant="h1">{translate('Checkout')}</Typography>
        </header>

        <article>
          <Preorder
            AppConfig={appConfig}
            ChangeDeliveryTime={handleChangeDeliveryTime}
            DeliveryLocation={deliveryLocation}
            disabled={disabled || loading}
            PickupLocationType={pickupLocationType}
            Restaurant={restaurant}
            type={params.type}
            RequestedDeliveryOrPickupTimeUtc={requestedTimeOrDefault}
          />

          <div>
            <i className="icon_cart" aria-hidden="true" />
            <Typography variant="h2">{translate('Your_basket')}</Typography>
            <Typography>
              {BasketControl.ToCurrency(totalToPay, currency, currentLang)}
            </Typography>
          </div>

          <FormGroup className="checkout__items">
            {itemList}
            {onlineProcessingCharge}
            {deliveryFeeRow}
            {taxesRow}
            {tipRow}
          </FormGroup>

          <Voucher orderPayload={orderPayload}>
            {renderVoucher(currency, isDriveThrough)}
          </Voucher>

          <FormGroup>
            <label htmlFor="checkoutName">
              {hasRequestFullNameFeatureFlag
                ? `${translate('Full_name')}`
                : `${translate('Name')}`}
              {`${
                params.type === 'delivery'
                  ? ' (' + translate('If_the_driver_needs_to_call') + ')'
                  : isTableServiceOnly
                  ? ''
                  : ' (' + translate('For_when_you_collect') + ')'
              }`}
            </label>
            <input
              data-fd="checkout-name"
              id="checkoutName"
              key="checkout-name"
              onChange={(e) => {
                setUserName({
                  name: e.currentTarget.value,
                });
              }}
              value={userName ? userName : ''}
            />
          </FormGroup>

          {restaurant.AllowChefNotes && (
            <FormGroup style={{ marginBottom: 0 }}>
              <label htmlFor="notesForChef">
                {translate('Notes_for_the_chef', {
                  categoryNote: translate(categoryNoteTranslation) as string,
                })}
              </label>
              <textarea
                data-fd="checkout-notes"
                id="notesForChef"
                onBlur={handleChefNoteBlur}
                onChange={handleChefNoteChange}
                placeholder={categoryNoteExample}
                value={checkoutUtils.getChefNote(
                  chefNote,
                  chefNotes,
                  restaurant,
                )}
              />
            </FormGroup>
          )}

          <SelectServiceButtons
            // checkoutState={this.state}
            pickupLocationOptions={pickupLocationOptions}
            pickupLocationType={pickupLocationType}
            serviceTypes={serviceTypes}
            tableNumber={tableNumber}
            tableServiceCategory={tableServiceCategory}
            toggleModal={toggleModal}
            vrid={params.vrid}
          />

          {/* color="error" */}
          {paymentError && !checkoutError && (
            <Typography>
              {translate(paymentError.message, undefined, {
                missingTranslationMsg: paymentError.message,
              })}
            </Typography>
          )}

          {/* color="error" */}
          {checkoutError && (
            <Typography>
              {translate(checkoutError.message, undefined, {
                missingTranslationMsg: checkoutError.message,
              })}
            </Typography>
          )}
          <PaymentOptions
            currency={currency}
            currentLanguage={currentLang}
            disabled={disabled}
            loading={loading}
            onSubmit={handleSubmit}
            payments={payments}
            price={totalToPay}
            selectedPayment={account}
            translate={translate}
          />
        </article>
      </Page>

      {customer.status === `success` && renderEmailModal(customer.data)}

      <CampaignConsentDialog customer={customer} />

      <TableSelectModal
        Close={toggleModal}
        handleNumber={handleNumberChange}
        handleSubmit={handleSubmit}
        number={tableNumber?.toString()}
        PickupLocationOptions={
          restaurant.DeliveryType === Api.DeliveryType.Collection
            ? restaurant.PickupLocationOptions
            : null
        }
        PlaceOrderAfterTableSelect={false}
        prid={params.id}
        ShowModal={showModal}
        TableServiceCategory={tableServiceCategory}
        translate={translate}
      />

      <SelectTimeModal
        show={missingTime || missingName || missingTableNumber}
        selectedPayment={account}
        loading={loading}
        disabled={false}
        translate={translate}
        onOk={continueCheckoutFlow}
        onCancel={cancelCheckoutFlow}
        preOrderTimeProps={
          missingTime && {
            isDelivery: params.type == 'delivery',
            isTableService: isTableServiceOnly,
            value: requestedTimeOrDefault,
            options:
              restaurant &&
              restaurant.PreOrderOptions &&
              restaurant.PreOrderOptions.PreOrderTimes,
            onChange: (time: string) => {
              setRequestedTime({
                time,
                menuId: menu.MenuId,
              });
            },
            translate: translate,
          }
        }
        nameInputProps={
          missingName && {
            isDelivery: params.type == 'delivery',
            isTableService: isTableServiceOnly,
            hasRequestFullNameFeatureFlag,
            value: userName,
            onChange: (name: string) => {
              setUserName({ name });
            },
            translate: translate,
          }
        }
        tableNumberInputProps={
          missingTableNumber && {
            value: this.props.TableNumber,
            category: tableServiceCategory,
            options:
              restaurant.DeliveryType === Api.DeliveryType.Collection
                ? restaurant.PickupLocationOptions
                : undefined,
            onChange: (tableNumber?: number) => {
              this.props.EditTableNumbers(params.vrid, tableNumber);
            },
            translate: this.props.translate,
          }
        }
      />
    </>
  );
}
